import { useCallback, useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { useUser } from '../../../Context/UserContext';

const POLL_INTERVAL_MS = 90_000;

const jsonHeaders = {
  Accept: 'application/json',
  'X-Requested-With': 'XMLHttpRequest',
};

interface UseSupportChatBadgeResult {
  hasUnread: boolean;
  refresh: () => Promise<void>;
}

export default function useSupportChatBadge(): UseSupportChatBadgeResult {
  const { user } = useUser();
  const userId = user?.id;
  const [hasUnread, setHasUnread] = useState(false);
  const cancelledRef = useRef(false);
  // Tracks the in-flight badge request so a slow earlier response cannot
  // overwrite state with stale data after a faster later response landed.
  const abortRef = useRef<AbortController | null>(null);

  const refresh = useCallback(async () => {
    if (!userId) return;
    abortRef.current?.abort();
    const controller = new AbortController();
    abortRef.current = controller;
    try {
      const response = await axios.get<{ has_unread: boolean }>(
        '/api/onlineshop/support-chat/has-unread',
        { headers: jsonHeaders, signal: controller.signal }
      );
      if (cancelledRef.current || controller.signal.aborted) return;
      setHasUnread(Boolean(response.data?.has_unread));
    } catch (e) {
      if (axios.isCancel(e)) return;
      console.error('support-chat badge:', e);
      // keep last known state — don't surface transient polling errors
    }
  }, [userId]);

  useEffect(() => {
    cancelledRef.current = false;
    return () => {
      cancelledRef.current = true;
      abortRef.current?.abort();
    };
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  useEffect(() => {
    if (!userId) return;
    const interval = window.setInterval(() => {
      if (!document.hidden) {
        refresh();
      }
    }, POLL_INTERVAL_MS);
    const onVisibility = () => {
      if (!document.hidden) {
        refresh();
      }
    };
    document.addEventListener('visibilitychange', onVisibility);
    return () => {
      window.clearInterval(interval);
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, [userId, refresh]);

  return { hasUnread, refresh };
}
